import React, { useCallback, useEffect } from 'react';
import { useUpdateTransaction } from 'src/hooks/queries/transactions';
import { AutoSaveViewModel } from 'src/types/view-models/auto-save.view.model';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField, useFormikContext } from 'formik';
import { TransactionModel } from 'src/types/transaction.model';
import { debounce } from 'lodash';
import TransactionService from 'src/services/transaction.service';

export default function AutoSaveTransaction({
    transaction,
    debounceMs = 300,
    baseFieldId = '',
    blockAutoSubmit = false, // if true then don't submit, just do update,
    handleCategoryChange
}: AutoSaveViewModel): JSX.Element {
    const { mutateAsync: updateTransaction } = useUpdateTransaction(transaction.id);
    const formik = useFormikContext<Partial<TransactionModel>>();
    const [, { value: valueGroupUid }]: [FieldInputProps<string>, FieldMetaProps<string>, FieldHelperProps<string>] =
        useField(`${baseFieldId}groupUid`);
    const [, { value: valueFrequency }]: [FieldInputProps<string>, FieldMetaProps<string>, FieldHelperProps<string>] =
        useField(`${baseFieldId}frequency`);

    // Debounce function ensures code is triggered once per user input

    /** Debounced callback function to update expense category of transaction after form submission */
    const debouncedUpdatedCategorySubmission = useCallback(
        debounce(() => {
            const maybeSubmit: Promise<void> = !blockAutoSubmit ? formik.submitForm() : Promise.resolve();
            void maybeSubmit
                .then(async () => await updateTransaction({ ...transaction, groupUid: valueGroupUid }))
                .then(() => {
                    if (handleCategoryChange) {
                        handleCategoryChange(valueGroupUid);
                    }
                });
        }, debounceMs),
        [debounceMs, valueGroupUid, formik.submitForm]
    );

    /** Debounced callback function to update frequency of transaction after form submission */
    const debouncedUpdatedFrequencySubmission = useCallback(
        debounce(() => {
            const maybeSubmit: Promise<void> = !blockAutoSubmit ? formik.submitForm() : Promise.resolve();
            void maybeSubmit.then(
                async () => await updateTransaction(TransactionService.updateFrequency(transaction, valueFrequency))
            );
        }, debounceMs),
        [debounceMs, valueFrequency, formik.submitForm]
    );

    /**  Updates the expense category of a transaction */
    useEffect(() => {
        void (async () => {
            if (!valueGroupUid || transaction.groupUid === valueGroupUid) return;
            await debouncedUpdatedCategorySubmission();
        })();
    }, [debouncedUpdatedCategorySubmission]);

    /**  Updates the frequency of a transaction */
    useEffect(() => {
        void (async () => {
            if (!valueFrequency || transaction.frequency === valueFrequency) return;
            await debouncedUpdatedFrequencySubmission();
        })();
    }, [debouncedUpdatedFrequencySubmission]);

    return <></>;
}
